import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { RouterLink } from '@angular/router';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, RowClickedEvent } from 'ag-grid-community';
import { IServerSideDatasource, IServerSideGetRowsParams } from 'ag-grid-enterprise';
import { debounceTime, distinctUntilChanged, Subject, Subscription } from 'rxjs';
import { AG_GRID_LOCALE_DE } from '../../../_helpers/aggrid.locale.de';
import { AgGridThemeService } from '../../../_services/ag-grid-theme.service';
import { ProjectService, StoreService } from '../../../_services';
import { APProject } from '../../../_models/project.interface';
import { StorePickerStore } from '../../../_models/store.interface';

/**
 * Wiederverwendbarer Filial-Picker als Modal mit AG-Grid.
 *
 * Das Grid läuft im Server-Side Row Model: AG-Grid fordert immer nur die gerade
 * sichtbare Seite an, und Sortierung wie Filterung passieren in der DB über den
 * gesamten Treffersatz. Damit bleibt der Picker auch bei zehntausenden Filialen
 * bedienbar, und eine Sortierung sortiert wirklich alles und nicht nur die
 * zufällig geladenen Zeilen.
 *
 * Vorbelegung und Sichtbarkeit der Filter sind getrennt: ein fest vorgegebener
 * Filter wird immer angewandt und nie als Bedienelement angezeigt. Ein Filter ohne
 * Vorgabe erscheint als Control — es sei denn, der zugehörige show*-Input blendet
 * ihn aus, weil er an dieser Einsatzstelle fachlich keinen Sinn ergibt.
 */
@Component({
  selector: 'app-store-picker',
  standalone: true,
  imports: [AgGridAngular, FormsModule, NgSelectModule, RouterLink],
  templateUrl: './store-picker.component.html',
})
export class StorePickerComponent implements OnInit, OnDestroy {
  /** Zeilen pro Grid-Seite. Muss der Blockgröße entsprechen, sonst passen Seite und Abfrage nicht zusammen. */
  protected static readonly PAGE_SIZE = 100;

  /** Projekt-Filter fest vorgeben. */
  @Input() projectIds: number[] | null = null;

  /** Einzelnes Projekt — Kurzform von projectIds für bestehende Aufrufer. */
  @Input() projectId: number | null = null;

  /** Projekt-Mehrfachauswahl anbieten, solange kein Projekt fest vorgegeben ist. */
  @Input() showProjectFilter = true;

  /** Vorbelegung der Volltextsuche, z.B. der Name des Technikers. */
  @Input() initialSearch = '';

  /** Filter auf Filialen ohne zugeordnete Versandfiliale fest vorgeben. */
  @Input() onlyUnassigned: boolean | null = null;

  /** Checkbox "nur freie Filialen" anbieten, solange der Filter nicht fest vorgegeben ist. */
  @Input() showUnassignedFilter = true;

  /**
   * Spalte "Zugeordnet an" anzeigen. Nur dort sinnvoll, wo es um die
   * Versandfiliale geht — sonst ist die Spalte für den Benutzer bedeutungslos.
   */
  @Input() showAssignedUserColumn = true;

  /**
   * Diese Filiale immer mitliefern, auch wenn sie durch "nur freie Filialen"
   * herausfallen würde — sonst verschwindet beim Bearbeiten die bereits
   * zugeordnete Filiale aus der Auswahl.
   */
  @Input() alwaysIncludeStoreId: number | null = null;

  /** Überschrift des Modals */
  @Input() heading = 'Filiale auswählen';

  /**
   * Kunden, mit denen eine hier neu angelegte Filiale verknüpft werden soll.
   * Ohne Angabe leitet die Anlege-Seite die Kunden aus den Projekten ab.
   */
  @Input() createStoreCustomerIds: number[] | null = null;

  /**
   * Den Suchbegriff als Namensvorschlag an die Anlege-Seite weiterreichen.
   * Nur dort sinnvoll, wo üblicherweise nach dem Filialnamen gesucht wird —
   * auf den Benutzerseiten etwa nach dem Namen des Technikers.
   */
  @Input() passSearchAsCreateStoreName = false;

  /** Emittiert die ausgewählte Filiale */
  @Output() storeSelected = new EventEmitter<StorePickerStore>();

  /** Emittiert beim Schließen ohne Auswahl */
  @Output() closed = new EventEmitter<void>();

  protected hawkTheme = inject(AgGridThemeService).getTheme();
  protected readonly locale = AG_GRID_LOCALE_DE;
  protected readonly pageSize = StorePickerComponent.PAGE_SIZE;
  private storeService = inject(StoreService);
  private projectService = inject(ProjectService);
  private gridApi?: GridApi;

  protected projects = signal<APProject[]>([]);

  /** Vom Benutzer im Modal gesetzte Filter (nur relevant, wenn kein Input sie fixiert) */
  protected searchTerm = signal<string>('');
  protected selectedProjectIds = signal<number[]>([]);
  protected unassignedChecked = signal<boolean>(false);

  private searchInput = new Subject<string>();
  private searchSubscription?: Subscription;

  /** In ngOnInit zusammengestellt, weil die Inputs die Spalten mitbestimmen. */
  protected colDefs: ColDef[] = [];

  /** Query-Parameter des "Filiale anlegen"-Links, siehe refreshCreateStoreQueryParams() */
  protected createStoreQueryParams: Record<string, number[] | string> = {};

  /**
   * Spaltenfilter bleiben aus — sie würden nur die geladene Seite betreffen.
   * Sortiert wird dagegen serverseitig, deshalb ist sortable an.
   */
  protected defaultColDef: ColDef = { filter: false, sortable: true };

  /** Liefert die Zeilen seitenweise nach — inklusive Sortierung aus dem Grid. */
  protected datasource: IServerSideDatasource = {
    getRows: (params: IServerSideGetRowsParams) => this.fetchRows(params),
  };

  /** Projekt-Filter ist frei wählbar, solange ihn kein Input festlegt */
  protected get projectFilterEditable(): boolean {
    return this.showProjectFilter && !this.projectIds?.length && this.projectId === null;
  }

  protected get unassignedFilterEditable(): boolean {
    return this.showUnassignedFilter && this.onlyUnassigned === null;
  }

  ngOnInit(): void {
    this.colDefs = this.buildColDefs();
    this.searchTerm.set(this.initialSearch);
    this.refreshCreateStoreQueryParams();

    if (this.projectFilterEditable) {
      this.loadProjects();
    }

    this.searchSubscription = this.searchInput
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(() => this.reload());
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  protected onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  protected onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
    // Nicht entprellt: der Link soll auch dann stimmen, wenn direkt nach dem
    // Tippen auf "Filiale anlegen" geklickt wird.
    this.refreshCreateStoreQueryParams();
    this.searchInput.next(value);
  }

  /** Filterwechsel wirkt sofort — nur die Texteingabe wird entprellt. */
  protected onFilterChanged(): void {
    this.refreshCreateStoreQueryParams();
    this.reload();
  }

  /**
   * Erneut laden, ohne Filter oder Suche anzufassen — gedacht für den Fall, dass
   * nebenbei in einem anderen Tab eine Filiale angelegt wurde.
   */
  protected refresh(): void {
    this.reload();
  }

  protected onRowClicked(event: RowClickedEvent): void {
    // Bei noch nicht geladenen Zeilen ist data undefined — dann nichts auswählen.
    if (!event.data) return;

    this.storeSelected.emit(event.data as StorePickerStore);
  }

  protected close(): void {
    this.closed.emit();
  }

  /**
   * Nach einem Filterwechsel zuerst auf Seite 1 springen und dann den Cache
   * verwerfen — sonst bliebe das Grid auf einer Seite stehen, die es im neuen
   * Treffersatz womöglich gar nicht mehr gibt.
   */
  private reload(): void {
    this.gridApi?.paginationGoToFirstPage();
    this.gridApi?.refreshServerSide({ purge: true });
  }

  private fetchRows(params: IServerSideGetRowsParams): void {
    const { startRow, endRow, sortModel } = params.request;
    const offset = startRow ?? 0;
    const sort = sortModel?.[0];

    this.storeService
      .searchStores({
        q: this.searchTerm().trim() || undefined,
        project_ids: this.activeProjectIds(),
        only_unassigned: this.unassignedActive(),
        include_store_id: this.alwaysIncludeStoreId ?? undefined,
        offset,
        limit: (endRow ?? offset + StorePickerComponent.PAGE_SIZE) - offset,
        sort_by: sort?.colId,
        sort_direction: sort?.sort as 'asc' | 'desc' | undefined,
      })
      .subscribe({
        next: result => {
          params.success({ rowData: result.stores, rowCount: result.total_count });
        },
        error: err => {
          console.error('Fehler beim Laden der Filialen:', err);
          params.fail();
        },
      });
  }

  private buildColDefs(): ColDef[] {
    const columns: ColDef[] = [
      { field: 'nr', headerName: 'Nr.', width: 130 },
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 180 },
      {
        field: 'address_street',
        headerName: 'Straße',
        flex: 1,
        minWidth: 180,
        valueGetter: params => {
          const store = params.data as StorePickerStore | undefined;
          if (!store) return '';

          return [store.address_street, store.address_street_no].filter(Boolean).join(' ');
        },
      },
      { field: 'address_zip', headerName: 'PLZ', width: 110 },
      { field: 'address_city', headerName: 'Ort', width: 160 },
    ];

    if (this.showAssignedUserColumn) {
      columns.push({
        colId: 'internal_shipping_user',
        headerName: 'Zugeordnet an',
        width: 170,
        valueGetter: params =>
          (params.data as StorePickerStore | undefined)?.internal_shipping_user?.name ?? '',
      });
    }

    return columns;
  }

  /**
   * Query-Parameter für die Filial-Anlage. Die aktuell wirksame Projekt-Auswahl
   * wird mitgegeben, damit die neue Filiale gleich richtig verknüpft wird. Ohne
   * Projekt-Filter bleiben die Parameter leer und dort ist alles frei wählbar.
   *
   * Bewusst ein Feld und kein Getter: routerLink würde bei einem Getter in jedem
   * Change-Detection-Zyklus eine neue Objekt-Referenz sehen und die URL neu bauen.
   */
  private refreshCreateStoreQueryParams(): void {
    const params: Record<string, number[] | string> = {};
    const projectIds = this.activeProjectIds();
    const searchTerm = this.searchTerm().trim();

    if (projectIds?.length) params['project_ids'] = projectIds;
    if (this.createStoreCustomerIds?.length) params['customer_ids'] = this.createStoreCustomerIds;
    if (this.passSearchAsCreateStoreName && searchTerm) params['name'] = searchTerm;

    this.createStoreQueryParams = params;
  }

  private activeProjectIds(): number[] | undefined {
    if (this.projectIds?.length) return this.projectIds;
    if (this.projectId !== null) return [this.projectId];
    return this.selectedProjectIds().length ? this.selectedProjectIds() : undefined;
  }

  private unassignedActive(): boolean {
    return this.onlyUnassigned ?? this.unassignedChecked();
  }

  private loadProjects(): void {
    this.projectService.getAPProjects().subscribe({
      next: projects => {
        this.projects.set(
          [...projects].sort((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : 1)),
        );
      },
      error: err => {
        console.error('Fehler beim Laden der Projekte:', err);
      },
    });
  }
}
